import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {IElementImage} from '../../../../incase-domain/elements/image';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import {generateErrorMessages, getErrorClasses} from '../../../../utils/validation';
import SectionTitle from '../../../elements/common/SectionTitle';
import HighlightedInputMedia from '../../../fileLoader/input.media';
import useImgFileReader from '../../../fileLoader/util/useImgFileReader';
import {TABS} from '../../form/part/tabs';
import AnimationForm from '../common/animationForm';
import BorderRadiusChange from '../common/borderRadiusChange';
import ElementShadow from '../common/elementShadow';
import SlideChange from '../common/slideChange';

interface HighlightedImageElementComponentProps {
    errors: IValidationErrorObject | null;
    overrideHighlightedElement?: IElementImage;
    overrideHighlightedElementChangeHandler?: {
        single: (filed: keyof IElementImage, value: unknown) => void;
        multiple?: (fields: (keyof IElementImage)[], values: unknown[]) => void;
    };
    updateImageHandler?: (fields: string[], values: unknown[]) => void;
    activeTab: string;
}

HighlightedImageElementComponent.defaultProps = {
    updateImageHandler: undefined,
};

function HighlightedImageElementComponent(props: HighlightedImageElementComponentProps): ReactElement {
    const {errors, overrideHighlightedElement, overrideHighlightedElementChangeHandler, updateImageHandler, activeTab} = props;
    const highlightedElement = useHighlightedElement() as IElementImage;
    const element = overrideHighlightedElement || highlightedElement;
    const updateHighlightedElement = useSetHighlightedElement();
    const changeHandler = overrideHighlightedElementChangeHandler || updateHighlightedElement;
    const {t} = useTranslation();

    const onFileLoad = useImgFileReader((images) => {
        const {imageSrc, widthHeightRatio} = images[0];
        updateImageHandler !== undefined
            ? updateImageHandler(['imageSrc', 'width', 'height', 'widthHeightRatio'], [imageSrc, '10%', 'auto', widthHeightRatio])
            : changeHandler.multiple?.(['imageSrc', 'width', 'height', 'widthHeightRatio'], [imageSrc, '10%', 'auto', widthHeightRatio]);
    });

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={element} highlightedElementChangeHandler={changeHandler} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <AnimationForm
                    appearAfter={element.appearAfter || 0}
                    elementAnimations={element.elementAnimations}
                    highlightedElementChangeHandler={changeHandler}
                />
                {updateImageHandler === undefined && (
                    <div className="l-group--form">
                        <div className="l-group__item">
                            <SlideChange
                                slideChange={element.slideChange}
                                setSlideChange={(slideChange) => {
                                    changeHandler.single('slideChange', slideChange);
                                }}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <div className={`l-group__item ${getErrorClasses(errors, 'imageSrc')}`}>
                    <HighlightedInputMedia type="image" hasExistingFile={!!(element as IElementImage).imageSrc} onLoad={onFileLoad} />

                    {generateErrorMessages(errors, 'imageSrc')}
                </div>
            </div>
            <div className="l-group--form">
                <SectionTitle label={t('designSidepanel.style')} />
                <BorderRadiusChange
                    borderRadius={element.borderRadius}
                    highlightedElementChangeHandler={(value) => changeHandler.single('borderRadius', value)}
                />
            </div>
        </>
    );
}

export default HighlightedImageElementComponent;
